import { useUTLSettingsContext } from "@dexteel/mesf-core";
import {
	Box,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Stack,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useEffect } from "react";
import { CurrentAndNextHeats } from "../models/CurrentAndNextHeats";
import { getCurrentHeats } from "../repositories/MillOverviewRepository";

export const useCurrentHeats = () => {
	return useQuery<CurrentAndNextHeats[], Error>({
		queryKey: ["currentHeats"],
		queryFn: ({ signal }) => getCurrentHeats(signal),
		refetchInterval: 30_000,
		refetchOnWindowFocus: true,
	});
};

export const HeatChips = ({
	setError,
}: {
	setError: (error: string) => void;
}) => {
	const { state } = useUTLSettingsContext();
	const showNextTons = get(state, `settings.FF_SHOW_NEXT_TONS`, false);
	const { data: currentHeats, isLoading, isError, error } = useCurrentHeats();

	useEffect(() => {
		if (error && isError) {
			setError(error.message);
		}
	}, [error, isError]);

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" width="100%">
				<CircularProgress size="1.5rem" />
			</Box>
		);
	}

	if (!currentHeats || currentHeats.length === 0) return null;

	const sorted = [...currentHeats].sort((a, b) => a.TagIndex - b.TagIndex);

	return (
		<Stack
			direction="row"
			spacing={1.5}
			flexWrap="wrap"
			alignItems="stretch"
			justifyContent="space-evenly"
		>
			{sorted.map((heat, index) => {
				const isCurrent = index === 0;

				return (
					<Card
						key={heat.TagIndex}
						variant={isCurrent ? "elevation" : "outlined"}
						elevation={isCurrent ? 3 : 0}
						sx={{
							position: "relative",
							minWidth: 80,
							bgcolor: isCurrent ? "primary.main" : "background.paper",
							color: isCurrent ? "primary.contrastText" : "text.primary",
							transition: "transform 0.15s ease, box-shadow 0.15s ease",
							"&:hover": {
								transform: "translateY(-1px)",
								elevation: isCurrent ? 6 : 2,
							},
						}}
					>
						{isCurrent && (
							<Chip
								label="Current"
								color="warning"
								size="small"
								sx={{
									position: "absolute",
									top: -1,
									right: -1,
									height: 18,
									fontSize: "0.5rem",
									fontWeight: 800,
									borderTopLeftRadius: 0,
									borderTopRightRadius: 0,
									borderBottomRightRadius: 0,
								}}
							/>
						)}

						<Box
							sx={{
								px: 1,
								py: 0.25,
								bgcolor: isCurrent ? "rgba(255,255,255,0.15)" : "grey.200",
								borderBottom: 1,
								borderColor: isCurrent ? "rgba(255,255,255,0.1)" : "divider",
							}}
						>
							<Typography
								variant="overline"
								sx={{
									fontSize: "0.6rem",
									opacity: isCurrent ? 0.9 : 0.7,
									lineHeight: 1,
								}}
							>
								{heat.SectionName || "—"}
							</Typography>
						</Box>

						<CardContent sx={{ px: 1, py: 0.5, "&:last-child": { pb: 0.5 } }}>
							<Typography
								variant="subtitle2"
								fontSize="0.85rem"
								fontWeight={700}
							>
								{heat.TagValue || "-"}
							</Typography>
						</CardContent>

						{showNextTons == "true" ? (
							<Box
								sx={{
									px: 1,
									bgcolor: isCurrent ? "rgba(0,0,0,0.1)" : "grey.50",
									borderTop: 1,
									borderColor: isCurrent ? "rgba(0,0,0,0.1)" : "divider",
									display: "flex",
									alignItems: "baseline",
									gap: 0.5,
								}}
							>
								<Typography variant="caption" fontSize="0.6 rem" lineHeight={1}>
									{heat.ScheduledRolledTons?.toLocaleString(undefined, {
										maximumFractionDigits: 1,
									}) ?? "—"}
								</Typography>
								<Typography
									variant="overline"
									sx={{ fontSize: "0.55rem", opacity: 0.7 }}
								>
									tons
								</Typography>
							</Box>
						) : null}
					</Card>
				);
			})}
		</Stack>
	);
};
